import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import config from "../config";
import { useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { useAuth } from "../context/AuthContext";
import { useSuccessFailure } from '../context/SuccessFailureContext';
import EventGrid from "../components/EventGrid";





const AllEventsPage = () => {
  const faviconUrl = process.env.REACT_APP_FAVICON_URL || '/favicon.ico';
  const eventTitle = process.env.REACT_APP_EVENT_TITLE || 'EventSawa';
  const eventDescription = process.env.REACT_APP_EVENT_DESCRIPTION || 'We make events sawa';

  const [events, setEvents] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);
  const { showSuccessModal, showFailureModal } = useSuccessFailure();

  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Fetch invitations and guests for the current event
  const fetchBulkInvitationsAndGuests = async () => {
    const token = localStorage.getItem("access_token");

    if (!token) {
      showFailureModal("Authentication token missing. Please log in.");
      return;
    }

    try {
      const response = await fetch(`${config.API_BASE_URL}/invitations/bulk/`, {
        method: "GET", // Assuming it's a GET request; adjust if needed
        headers: {
          Authorization: `Bearer ${token}`, // Add the user token if necessary
        },
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      // console.log("Bulk invitation and guest data:", data);

      if (data) {
        setInvitations(data.invitations || []);
        setGuests(data.guests || []);
      } else {
        showFailureModal("Failed to fetch invitations and guests.");
      }
    } catch (error) {
      // console.error("Error fetching invitations and guests:", error);
      showFailureModal("Error fetching invitations and guests.");
    }
  };

  // Fetch events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        // Use fetch instead of axios to avoid any interceptors
        const response = await fetch(`${config.API_BASE_URL}/events/all/no-pagination/`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            // Explicitly exclude any authorization headers
          },
          credentials: 'omit' // Ensure no credentials are sent
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        // Extract events from the response
        const eventsData = data.events || [];
        setEvents(eventsData);
      } catch (error) {
        console.error("Error fetching events:", error);
        setError(error.message || "Failed to fetch events");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Fetch invitations and guests after events are loaded
  useEffect(() => {
    if (user && events.length > 0) {
      fetchBulkInvitationsAndGuests();
    }
  }, [events, user]);

  // Memoized filtered events
  const filteredEvents = useMemo(() => {
    // If events is not an array, return empty array
    if (!Array.isArray(events)) {
      return [];
    }

    return events
      .slice()
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .filter((event) => {
        const searchMatch =
          (event.title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (event.description || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
          (Array.isArray(event.keywords) ? event.keywords : []).some((keyword) =>
            keyword.toLowerCase().includes(searchQuery.toLowerCase())
          );

        const isPublic = event.visibility === "public";
        if (!user) {
          return searchMatch && isPublic;
        }

        const isCreator = user?.id === event.created_by;
        const isInvited = invitations.some(
          (invitation) => invitation.event === event.id && invitation.recipient === user.id
        );

        return searchMatch && (isPublic || isCreator || isInvited);
      });
  }, [events, searchQuery, user, invitations]);

  // Navigate to event
  const handleEventClick = (eventSlug) => {
    navigate(`/events/${eventSlug}`);
  };

  // Show loading indicator
  if (authLoading || loading) {
    return <p className="text-gray-500 dark:text-gray-400">Loading...</p>;
  }

  return (
    <div className="bg-gray-100 dark:bg-gray-900 min-h-screen">
      <Helmet>
        <link rel="icon" href={faviconUrl} />
        <title>{eventTitle}</title>
        <meta name="description" content={eventDescription} />
      </Helmet>

      {/* Events Section */}
      <div className="max-w-screen-xl mx-auto">
        <EventGrid
          events={events}
          invitations={invitations}
          user={user}
          onEventClick={handleEventClick}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
      </div>
    </div>
  );
};

export default AllEventsPage;